import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Shield, 
  Activity, 
  CheckCircle, 
  AlertTriangle, 
  XCircle,
  RefreshCw,
  Wifi,
  WifiOff,
  Clock,
  Server
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function SystemStatus() {
  const { token } = useAuth();
  const [healthSummary, setHealthSummary] = useState(null);
  const [healthEvents, setHealthEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orgId, setOrgId] = useState(null);

  useEffect(() => {
    fetchOrgAndHealth();
  }, [token]);

  const fetchOrgAndHealth = async () => {
    try {
      setLoading(true);
      
      // First get user's org
      const orgsRes = await fetch(`${API_URL}/api/org/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const orgs = await orgsRes.json();
      
      if (orgs.length > 0) {
        const firstOrgId = orgs[0].id;
        setOrgId(firstOrgId);
        
        // Fetch health summary
        const summaryRes = await fetch(`${API_URL}/api/system-health/${firstOrgId}/summary`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (summaryRes.ok) {
          const summary = await summaryRes.json();
          setHealthSummary(summary);
        }
        
        // Fetch health events
        const eventsRes = await fetch(`${API_URL}/api/system-health/${firstOrgId}/events?limit=20`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (eventsRes.ok) {
          const events = await eventsRes.json();
          setHealthEvents(events);
        }
      }
    } catch (err) {
      setError('Unable to load system status');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="status-icon healthy" size={24} />;
      case 'degraded':
        return <AlertTriangle className="status-icon degraded" size={24} />;
      case 'critical':
        return <XCircle className="status-icon critical" size={24} />;
      default:
        return <Activity className="status-icon" size={24} />;
    }
  };

  const getIntegrationIcon = (connected) => {
    return connected ? (
      <Wifi className="integration-icon connected" size={18} />
    ) : (
      <WifiOff className="integration-icon disconnected" size={18} />
    );
  };

  const getSeverityClass = (severity) => {
    switch (severity) {
      case 'critical': return 'severity-critical';
      case 'error': return 'severity-error';
      case 'warning': return 'severity-warning';
      default: return 'severity-info';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="support-page">
        <div className="support-container">
          <div className="loading-state">
            <RefreshCw className="spin" size={24} />
            <p>Loading system status...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="support-page" data-testid="system-status-page">
      <div className="support-container">
        <Link to="/support" className="support-back-link">
          <ArrowLeft size={16} />
          Back to Support
        </Link>

        <div className="support-header">
          <div className="support-icon">
            <Activity size={28} />
          </div>
          <h1>System Status</h1>
          <p className="support-subtitle">
            Read-only view of your organisation's system health
          </p>
        </div>

        {error && (
          <div className="support-error">
            <AlertTriangle size={18} />
            <span>{error}</span>
          </div>
        )}

        {healthSummary && (
          <>
            {/* Overall Status */}
            <div className="support-card status-overview" data-testid="status-overview">
              <div className="status-header">
                {getStatusIcon(healthSummary.overall_status)}
                <div className="status-info">
                  <h2 className={`status-title ${healthSummary.overall_status}`}>
                    System {healthSummary.overall_status === 'healthy' ? 'Healthy' : 
                           healthSummary.overall_status === 'degraded' ? 'Degraded' : 'Critical'}
                  </h2>
                  <p className="status-subtitle">
                    Last checked: {formatDate(healthSummary.last_checked_at)}
                  </p>
                </div>
              </div>

              <div className="status-metrics">
                <div className="metric">
                  <span className="metric-value">{healthSummary.pending_issues}</span>
                  <span className="metric-label">Pending Issues</span>
                </div>
                <div className="metric">
                  <span className="metric-value critical">{healthSummary.critical_issues}</span>
                  <span className="metric-label">Critical</span>
                </div>
                <div className="metric">
                  <span className="metric-value">{healthSummary.data_completeness_score?.toFixed(0) || 0}%</span>
                  <span className="metric-label">Data Score</span>
                </div>
              </div>
            </div>

            {/* Integrations Status */}
            <div className="support-card" data-testid="integrations-status">
              <h3 className="card-title">
                <Server size={18} />
                Integration Status
              </h3>
              <div className="integrations-list">
                {healthSummary.integrations?.map((integration, index) => (
                  <div key={index} className={`integration-item ${integration.connected ? 'connected' : 'disconnected'}`}>
                    {getIntegrationIcon(integration.connected)}
                    <div className="integration-info">
                      <span className="integration-name">{integration.name}</span>
                      <span className="integration-status">{integration.status.replace('_', ' ')}</span>
                    </div>
                    {integration.last_sync_at && (
                      <span className="integration-sync">
                        <Clock size={12} />
                        {formatDate(integration.last_sync_at)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Health Events */}
            {healthEvents.length > 0 && (
              <div className="support-card" data-testid="health-events">
                <h3 className="card-title">
                  <AlertTriangle size={18} />
                  Recent Health Events
                </h3>
                <div className="events-list">
                  {healthEvents.map((event) => (
                    <div key={event.id} className={`event-item ${getSeverityClass(event.severity)}`}>
                      <div className="event-header">
                        <span className={`event-severity ${event.severity}`}>
                          {event.severity.toUpperCase()}
                        </span>
                        <span className="event-source">{event.source}</span>
                        <span className="event-time">{formatDate(event.timestamp)}</span>
                      </div>
                      <p className="event-description">{event.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Trust Notice */}
        <div className="support-trust-notice" data-testid="trust-notice">
          <Shield size={18} />
          <p>
            TradeLife support can only access technical system status — never your financial data or documents. 
            Any access is logged and visible to you.
          </p>
        </div>
      </div>
    </div>
  );
}

export default SystemStatus;
